import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Play, Zap, Lock, DollarSign, Users, Terminal, Cpu, Clock, Fingerprint, Globe, Tag, Activity, ArrowUpRight, Radio, Hash, Plus, Brain, Key, Network, Volume2, VolumeX, Loader2, Check, X } from 'lucide-react';
import { userService } from '../services/UserService';
import { referralService } from '../services/ReferralService';
import { assetUrl } from '../config/assets';

const ACCESS_CODE_KEY = 'lunexx_access_granted';
const REFERRAL_CODE_KEY = 'lunexx_referral_code';

// 040-01: every landing <video> carries a poster served from the app's own
// origin, so a panel paints its first frame before any video byte arrives
// from the media origin — never a black box while the file loads.
const posterUrl = (name) => `${process.env.PUBLIC_URL}/landing/posters/${name}.jpg`;

export default function KolabLanding({ showAccessGate = true }) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(0);
  const [newRequest, setNewRequest] = useState('');
  const [amount, setAmount] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredReq, setHoveredReq] = useState(null);
  const [accessKey, setAccessKey] = useState('');
  const [status, setStatus] = useState('IDLE'); // IDLE, VALIDATING, GRANTED, ERROR
  const [errorMessage, setErrorMessage] = useState('');
  const [activeFeature, setActiveFeature] = useState(0);
  const hasAutoValidated = useRef(false);

  // Simulation of "typing" effect for the placeholder
  const [placeholder, setPlaceholder] = useState('');
  const fullText = "enter_neural_id_or_email...";
  // AUDIO CONTROL STATES
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const [heroBlocked, setHeroBlocked] = useState(false);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const [warmRequests, setWarmRequests] = useState(false);

  // ── Below-the-fold media gating ───────────────────────────────────────
  // Only the hero loads eagerly. Everything else below the fold used to
  // start downloading at mount, competing with the 1.12 MB hero for the ~6
  // connections a browser opens to one HTTP/1.1 origin — on a phone the hero
  // lost that race often enough to sit black.
  //
  //   necklaceRef  ModelNecklace.mp4  2.24 MB
  //   viewportRef  backstage_sm.mp4   0.96 MB (the viewport box; since 040-01
  //                also the four request videos, mounted with it)
  //   talentRef    the three TALENT card images, 1.61 MB together
  //
  // preload="none" does NOT fix the videos: with `autoplay` present the UA
  // must fetch data to satisfy it and treats preload as a hint it may
  // ignore. loading="lazy" does NOT fix the images either, and the reason is
  // worth writing down: Chrome only defers an image that starts beyond its
  // lazy-load distance threshold, measured here at ~2110px (about 2.5
  // viewport heights) on a fast connection. The TALENT grid begins one
  // viewport down, so its images sit at 1111/1587/2063px at 390x844 and
  // 1124px at every desktop width — all inside the threshold. Measured:
  // loading="lazy" deferred 0 of 3 at 390, 768, 1280 and 1440. A slower
  // connection only makes Chrome's threshold larger, never smaller.
  //
  // The thing that actually defers bytes is withholding `src`, because an
  // element with no source makes no request at all. Same shape as
  // CreatorReelsRow, which mounts a <video> only for the active tile. The
  // card has a solid bg-[#0A0A0A] and a fixed h-[450px]/md:h-[520px], so an
  // image arriving late fills a dark box that was already the right size —
  // no layout shift, no white flash.
  //
  // Latching: once near, stay near. Re-clearing src mid-scroll would restart
  // the download and reset playback.
  const necklaceRef = useRef(null);
  const viewportRef = useRef(null);
  const talentRef = useRef(null);
  const [necklaceNear, setNecklaceNear] = useState(false);
  const [viewportNear, setViewportNear] = useState(false);
  const [talentNear, setTalentNear] = useState(false);

  useEffect(() => {
    // threshold 0.01, rootMargin 0 — and both parts are load-bearing.
    //
    // The default threshold of 0 reports isIntersecting for an element whose
    // edge merely TOUCHES the root, and the next section begins exactly at
    // the fold, so a plain observer fired for ModelNecklace at page load and
    // deferred nothing. Requiring 1% real overlap fixes that.
    //
    // A rootMargin head start cannot be combined with it: expanding the root
    // by N px puts N px of a fold-adjacent element inside the root, which is
    // well over 1% of a 250px-tall box, so any positive margin re-triggers at
    // load. Measured at 390x844: margin 0 defers 3.20 MB, every positive
    // margin tried defers only 0.96 MB. The trade is that these two start
    // loading as they scroll in rather than slightly before — the right way
    // round for 2.24 MB of decorative background.
    const pairs = [
      [necklaceRef, setNecklaceNear],
      [viewportRef, setViewportNear],
      [talentRef, setTalentNear],
    ];
    const observers = pairs.map(([ref, set]) => {
      const el = ref.current;
      if (!el) return null;
      const io = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) { set(true); io.disconnect(); } },
        { rootMargin: '0px', threshold: 0.01 },
      );
      io.observe(el);
      return io;
    });
    return () => observers.forEach((io) => io && io.disconnect());
  }, []);

  // ── Hero autoplay ──────────────────────────────────────────────────
  // `autoPlay muted playsInline` in the JSX is not a guarantee, and the
  // usual explanation for that is wrong, so it is worth recording what was
  // actually measured.
  //
  // React does not emit a `muted` ATTRIBUTE: react-dom 18.3.1 lists `muted`
  // in its mustUseProperty set, so it assigns node.muted and never calls
  // setAttribute. The rendered hero really is <video autoplay loop
  // playsinline> with no muted attribute. That is usually blamed for mobile
  // autoplay failing — but tested in Chromium under
  // --autoplay-policy=user-gesture-required, with the DOM built the way
  // React builds it (src assigned before muted, property only, appended
  // after), the video autoplays. An unmuted control under the same policy
  // stays paused, so the policy was being enforced. The missing attribute
  // blocks nothing there.
  //
  // What actually stops a muted hero on a phone is the part no attribute
  // controls: iOS Low Power Mode refuses autoplay outright, Data Saver can,
  // and a stalled load never reaches a playable state. None of those can be
  // detected in advance and all of them look the same to the user — a black
  // hero. So rather than guess the cause, attempt play, notice the failure,
  // and offer a tap. `muted` is still asserted on the element below as free
  // insurance for older WebKit, which read the attribute at load time; it is
  // not the fix for anything observed here.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');

    let cancelled = false;
    const attempt = () => {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { if (!cancelled) setHeroBlocked(false); })
          .catch(() => { if (!cancelled) setHeroBlocked(true); });
      }
    };

    attempt();
    // A phone often has nothing decodable yet on the first attempt; retry
    // once there is, which is the common case for a slow first load.
    v.addEventListener('loadeddata', attempt);
    return () => {
      cancelled = true;
      v.removeEventListener('loadeddata', attempt);
    };
  }, []);

  // ── Request-card viewport (040-01) ─────────────────────────────────
  // The viewport used to be ONE <video> keyed by the hovered card, so every
  // hover remounted it and refetched a 1–2.4 MB file from an uncached
  // origin: a black box until the whole file arrived. Now all five (the
  // backstage default + the four request videos) are mounted together once
  // the viewport is near and stacked in the same box; hover only swaps which
  // one is visible and playing. No key, no src swap, no remount, so a second
  // hover of the same card replays what is already buffered.
  //
  // Warm-up: the four request videos start at preload="metadata" (a few KB
  // with faststart files) and move to preload="auto" once the hero is
  // playing — or 3 s after the viewport is near, whichever comes first — in
  // an idle callback, so they buffer before the first hover without
  // competing with the hero.
  useEffect(() => {
    if (!viewportNear || warmRequests) return undefined;
    let idleId = null;
    let idleTimer = null;
    const warm = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => setWarmRequests(true));
      } else {
        idleTimer = setTimeout(() => setWarmRequests(true), 0);
      }
    };
    const delay = heroPlaying ? null : setTimeout(warm, 3000);
    if (heroPlaying) warm();
    return () => {
      clearTimeout(delay);
      clearTimeout(idleTimer);
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [viewportNear, heroPlaying, warmRequests]);

  useEffect(() => {
    const box = viewportRef.current;
    if (!viewportNear || !box) return;
    const active = hoveredReq ? String(hoveredReq.id) : 'default';
    box.querySelectorAll('video[data-viewport-video]').forEach((v) => {
      if (v.dataset.viewportVideo === active) {
        if (active !== 'default') v.currentTime = 0;
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => { });
      } else {
        v.pause();
      }
    });
  }, [hoveredReq, viewportNear]);

  const startHero = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => setHeroBlocked(false)).catch(() => { });
    } else {
      setHeroBlocked(false);
    }
  };

  // Check for existing access on mount (only if showing access gate)
  useEffect(() => {
    if (!showAccessGate) return;

    const hasAccess = localStorage.getItem(ACCESS_CODE_KEY);
    if (hasAccess === 'true') {
      setStatus('GRANTED');
      // Auto-redirect after brief delay
      setTimeout(() => {
        navigate('/discover');
      }, 500);
    }
  }, [navigate, showAccessGate]);

  // Capture referral code from URL and AUTO-VALIDATE on mount
  useEffect(() => {
    if (!showAccessGate) return;
    if (hasAutoValidated.current) return;

    const refCode = referralService.getReferralCodeFromURL();
    if (refCode) {
      hasAutoValidated.current = true;
      const normalizedCode = refCode.toUpperCase();
      setAccessKey(normalizedCode);
      localStorage.setItem(REFERRAL_CODE_KEY, normalizedCode);

      // Auto-validate the referral code silently
      autoValidateReferralCode(normalizedCode);
    }
  }, [showAccessGate]);

  // Auto-validate referral code - grants access without redirecting
  const autoValidateReferralCode = async (code) => {
    setStatus('VALIDATING');

    try {
      const referralResult = await referralService.validateReferralCode(code);

      if (referralResult.valid) {
        // Grant access - user can now explore the landing page
        // When they're ready, they'll click through or get auto-redirected
        localStorage.setItem(ACCESS_CODE_KEY, 'true');
        localStorage.setItem('lunexx_access_code', code);

        if (referralResult.isReferral) {
          localStorage.setItem(REFERRAL_CODE_KEY, code);
        }

        setStatus('GRANTED');

        // Clean up URL params without redirecting
        if (window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.delete('ref');
          url.searchParams.delete('referral');
          window.history.replaceState({}, '', url.pathname);
        }

        // Redirect to app after showing "Access Granted" briefly
        setTimeout(() => {
          navigate('/discover');
        }, 1200);

        return;
      }

      // Invalid referral code - reset to let user try manually
      setStatus('IDLE');
      setErrorMessage('');
    } catch (err) {
      console.error('Auto-validation error:', err);
      // On error, just reset - don't show error for auto-validation
      setStatus('IDLE');
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setPlaceholder(fullText.slice(0, i));
      i++;
      if (i > fullText.length) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!accessKey.trim()) {
      setErrorMessage('Please enter an access code');
      setStatus('ERROR');
      return;
    }

    setStatus('VALIDATING');
    setErrorMessage('');

    try {
      // First try validating as referral code
      const referralResult = await referralService.validateReferralCode(accessKey);

      if (referralResult.valid) {
        // Store access granted and the code used
        localStorage.setItem(ACCESS_CODE_KEY, 'true');
        localStorage.setItem('lunexx_access_code', accessKey.toUpperCase().trim());

        // If it's a referral code, store it for signup
        if (referralResult.isReferral) {
          localStorage.setItem(REFERRAL_CODE_KEY, accessKey.toUpperCase().trim());
        }

        setStatus('GRANTED');

        // Navigate to app after brief delay
        setTimeout(() => {
          navigate('/discover');
        }, 1500);

        return;
      }

      // Fallback to regular access code validation
      const { valid, error: validationError } = await userService.validateAccessCode(accessKey);

      if (!valid) {
        setErrorMessage(validationError?.message || 'Invalid access code');
        setStatus('ERROR');
        return;
      }

      // Mark code as used
      await userService.useAccessCode(accessKey);

      // Store access granted
      localStorage.setItem(ACCESS_CODE_KEY, 'true');
      localStorage.setItem('lunexx_access_code', accessKey.toUpperCase().trim());

      setStatus('GRANTED');

      // Navigate to app after brief delay
      setTimeout(() => {
        navigate('/discover');
      }, 1500);
    } catch (err) {
      console.error('Access code validation error:', err);
      setErrorMessage('Something went wrong. Please try again.');
      setStatus('ERROR');
    }
  };

  // Reset error state when user types
  const handleInputChange = (e) => {
    setAccessKey(e.target.value.toUpperCase());
    if (status === 'ERROR') {
      setStatus('IDLE');
      setErrorMessage('');
    }
  };

  const [activeItem, setActiveItem] = useState(0);

  const features = [
    {
      id: 0,
      icon: Network,
      title: "Invisible Curation",
      desc: "AI learns your preferences to surface talent that matches your taste and unstated desires.",
      stat: "Nodes: 8.4M",
      metric: "Latency: 4ms"
    },
    {
      id: 1,
      icon: Zap,
      title: "Fantasy Sessions",
      desc: "The experience evolves in real-time, intelligently adapting to your reactions and desires — without you asking.",
      stat: "Updates: 24/s",
      metric: "Flow: 100%"
    },
    {
      id: 2,
      icon: Brain, // Or Shield/Fingerprint if preferred
      title: "Wallet-Only Identity",
      desc: "Your wallet is your only ID. All preferences are tied to your cryptographic keys — never your identity.",
      stat: "Encrypted",
      metric: "Zero-Knowledge"
    }
  ];


  // Mock Data for the "Bounty Board"
  const [activeRequests, setActiveRequests] = useState([
    {
      id: 1,
      title: "Switch to night vision mode",
      backers: 42,
      current: "0.12",
      trending: true,
      type: "CRITICAL",
      icon: Zap,
      video: assetUrl('Robe.mp4'),
      poster: posterUrl('Robe')
      //https://kolagentimages.s3.us-east-2.amazonaws.com/nightvision_sm.mp4
    },
    {
      id: 2,
      title: "Clip and tug the collar",
      backers: 128,
      current: "0.1",
      trending: true,
      type: "AUDIO",
      icon: Radio,
      video: assetUrl('collarnew.mp4'),
      poster: posterUrl('collarnew')
    },
    {
      id: 3,
      title: "Give me a view that lingers",
      backers: 15,
      current: "0.3",
      trending: false,
      type: "INTERRUPT",
      icon: Globe,
      video: assetUrl('geishathree_sm.mp4'),
      poster: posterUrl('geishathree_sm')
    },
    {
      id: 4,
      title: "Give me a private crypto lesson",
      backers: 310,
      current: "0.25",
      trending: true,
      type: "ACCESS",
      icon: Lock,
      video: assetUrl('hottub_vfsm.mp4'),
      poster: posterUrl('hottub_vfsm')
    }
  ]);

  const transactions = [
    { id: '0x8a...4b2', action: 'TICKET_RESALE', target: 'Room: The Obsidian', value: '+0.4 ETH', time: '2s ago' },
    { id: '0x9c...3d1', action: 'ACTION_REQ', target: 'Model: Katia', value: '-0.5 ETH', time: '4s ago' },
    { id: '0x1f...9a8', action: 'ENTRY_MINT', target: 'User: 0x33...89', value: '0.03 ETH', time: '8s ago' },
    { id: '0x7b...2e4', action: 'YIELD_PAYOUT', target: 'Creator: Protocol', value: '+1.5 ETH', time: '12s ago' },
    { id: '0x5e...8f0', action: 'ACTION_REQ', target: 'Model: Unit_01', value: '-1.2 ETH', time: '21s ago' },
  ];

  return (
    <div
      id="scroll-root"
      className="h-screen w-full overflow-y-auto overflow-x-hidden bg-[#000000] text-white selection:bg-[#A3E635] selection:text-black font-sans scroll-smooth"
      onScroll={(e) => setScrolled(e.currentTarget.scrollTop)}
    >
      {/* HERO SECTION */}
      <section className="relative h-screen w-full bg-black overflow-hidden">

        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            src={assetUrl('hero-new_sm.mp4')}
            poster={posterUrl('hero-new_sm')}
            onPlaying={() => setHeroPlaying(true)}
            className="w-full h-full object-cover md:object-cover object-[center_30%] opacity-60"
            autoPlay
            loop
            muted
            playsInline
          />
          {/* Gradient overlay - lighter on mobile to show more video */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 md:from-black/90 via-transparent to-black/50 md:to-transparent" />
        </div>

        {/* Autoplay fallback. Renders only when play() was refused, so the
            default path is byte-identical to before. Deliberately a small
            corner control, not a full-cover scrim: an inset-0 overlay at
            this z would take the taps meant for the brand and the gate. */}
        {heroBlocked && (
          <button
            type="button"
            onClick={startHero}
            className="absolute bottom-0 left-0 m-6 md:m-16 z-20 flex items-center gap-2 rounded-full border border-white/15 bg-black/60 px-4 py-2.5 backdrop-blur-xl transition-colors hover:bg-black/80"
          >
            <Play size={14} className="text-[#A3E635]" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-white/90">
              Play
            </span>
          </button>
        )}

        {/* 2. TOP LEFT: THE BRAND */}
        <div className="absolute top-0 left-0 p-6 md:p-16 z-10">
          <h1 className="text-5xl md:text-8xl lg:text-[7rem] font-bold text-white tracking-tight leading-none drop-shadow-2xl">
            LUNEXX<span className="text-[#A3E635]">.</span>
          </h1>
        </div>

        {/* 3. TOP RIGHT (Access Gate) OR BOTTOM RIGHT (Enter App Button) */}
        <div className={`absolute ${showAccessGate ? 'top-0 mt-2 md:mt-5' : 'bottom-0'} right-0 p-6 md:p-16 z-20 w-full max-w-[320px] md:max-w-[420px] flex flex-col items-end`}>

          {showAccessGate ? (
            // ACCESS GATE MODE (app.lunexx.ai)
            <>
              {/* THE GLASS CAPSULE */}
              <div className={`w-full backdrop-blur-xl bg-[#050505]/60 border rounded-2xl shadow-2xl overflow-hidden transition-all duration-500 group
                ${status === 'GRANTED'
                  ? 'border-[#A3E635]/50 shadow-[0_0_40px_rgba(163,230,53,0.15)]'
                  : status === 'ERROR'
                    ? 'border-red-500/50 shadow-[0_0_40px_rgba(239,68,68,0.1)]'
                    : 'border-white/10 hover:border-white/20 hover:bg-[#050505]/80'}`}>

                {status === 'GRANTED' ? (
                  // GRANTED STATE
                  <div className="h-[60px] flex items-center justify-center animate-fade-in bg-[#A3E635]/5">
                    <div className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-[#A3E635]" />
                      <span className="text-[#A3E635] font-mono text-xs uppercase tracking-[0.2em]">
                        Access Granted
                      </span>
                    </div>
                  </div>
                ) : (
                  // INPUT STATE
                  <form onSubmit={handleSubmit} className="relative flex items-center p-2 h-[60px]">

                    {/* Status indicator */}
                    <span className={`absolute left-6 font-mono text-lg transition-colors ${status === 'ERROR' ? 'text-red-500' : 'text-zinc-600 animate-pulse'
                      }`}>
                      {status === 'VALIDATING' ? '' : '›'}
                    </span>

                    {status === 'VALIDATING' ? (
                      <div className="absolute left-5">
                        <Loader2 className="w-5 h-5 text-[#A3E635] animate-spin" />
                      </div>
                    ) : null}

                    <input
                      type="text"
                      value={accessKey}
                      onChange={handleInputChange}
                      className={`w-full bg-transparent font-mono text-[13px] outline-none pl-10 pr-4 uppercase tracking-widest h-full transition-colors ${status === 'ERROR'
                        ? 'text-red-400 placeholder:text-red-500/50'
                        : 'text-white placeholder:text-zinc-600/70'
                        }`}
                      placeholder="ENTER_ACCESS_KEY"
                      autoFocus
                      autoComplete="off"
                      spellCheck="false"
                      disabled={status === 'VALIDATING'}
                    />

                    {/* High-Contrast Action Button */}
                    <button
                      type="submit"
                      disabled={!accessKey || status === 'VALIDATING'}
                      className={`h-11 w-11 flex-shrink-0 flex items-center justify-center rounded-xl transition-all duration-500 cubic-bezier(0.23, 1, 0.32, 1)
                      ${status === 'VALIDATING'
                          ? 'bg-zinc-800 cursor-not-allowed opacity-50'
                          : 'bg-[#A3E635] text-black hover:scale-105 hover:bg-[#b5e865]'}
                      ${accessKey ? 'opacity-100 translate-x-0 rotate-0' : 'opacity-0 translate-x-4 rotate-90 pointer-events-none'}`}
                    >
                      {status === 'VALIDATING' ? (
                        <Loader2 size={18} className="animate-spin text-white" />
                      ) : (
                        <ArrowRight size={20} />
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Error Message */}
              {status === 'ERROR' && errorMessage && (
                <div className="mt-3 flex items-center gap-2 text-red-400 text-xs font-mono animate-fade-in">
                  <X size={12} />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Hint text */}
              {status === 'IDLE' && !accessKey && (
                <p className="mt-3 text-zinc-600 text-[10px] font-mono uppercase tracking-wider">
                  Early access only
                </p>
              )}
            </>
          ) : (
            // MARKETING MODE (lunexx.ai) - "Enter App" button
            <a
              href={(() => {
                // Preserve referral code when navigating to app
                const refCode = localStorage.getItem(REFERRAL_CODE_KEY) || new URLSearchParams(window.location.search).get('ref');
                const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
                  ? '/'
                  : 'https://app.lunexx.ai';
                return refCode ? `${baseUrl}?ref=${refCode}` : baseUrl;
              })()}
              onClick={(e) => {
                // On localhost, remove the ?mode=landing param to show access gate
                if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                  e.preventDefault();
                  const refCode = localStorage.getItem(REFERRAL_CODE_KEY) || new URLSearchParams(window.location.search).get('ref');
                  window.location.href = refCode ? `/?ref=${refCode}` : '/';
                }
              }}
              className="group flex items-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 bg-white/5 backdrop-blur-xl border border-[#A3E635]/50 text-[#A3E635] font-mono text-xs md:text-sm uppercase tracking-widest rounded-xl md:rounded-2xl hover:bg-[#A3E635]/10 hover:border-[#A3E635] hover:shadow-[0_0_40px_rgba(163,230,53,0.2)] transition-all duration-500"
            >
              <span>Enter App</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          )}

        </div>

      </section>

      {/* SECTION 2: THE TALENT */}
      <section className="min-h-screen bg-[#050505] py-20 md:py-32 px-6 md:px-16 relative overflow-hidden">

        {/* Ambient Mystery Glow */}
        <div className="absolute top-[20%] right-0 w-[800px] h-[800px] bg-[#A3E635] rounded-full blur-[300px] opacity-[0.03] pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">

          {/* MOBILE: Centered | DESKTOP: Space-between */}
          <header className="mb-12 md:mb-24 flex flex-col md:flex-row justify-between items-center md:items-end gap-6 md:gap-10 text-center md:text-left">
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em]">
                  Premium Creators
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.9]">
                THE <span className="text-white">TALENT</span><span className="text-[#A3E635]">.</span>
              </h2>
            </div>

            <div className="max-w-md">
              <p className="text-zinc-400 text-sm font-mono font-normal leading-loose tracking-wide">
                Access a curated network of creators — <span className="text-zinc-200">real humans</span> and <span className="text-zinc-200">next-gen AI</span>.
              </p>
            </div>
          </header>

          <div ref={talentRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            {[
              {
                name: "Zeya Maren",
                type: "AI",
                img: assetUrl('models/ZeyaMaren.jpeg'),
                followers: "16K",
                vol: "19 ETH"
              },
              {
                name: "Kira Tanaka",
                type: "AI",
                img: assetUrl('KiraTanaka1.png'),
                followers: "1.1k",
                vol: "17 ETH"
              },
              {
                name: "Talia Ren",
                type: "AI",
                img: assetUrl('model7cut.png'),
                followers: "6K",
                vol: "11 ETH"
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="relative group block"
              >

                {/* THE GLOW */}
                <div className="absolute -inset-2 bg-[#A3E635] rounded-[32px] blur-[60px] opacity-0 group-hover:opacity-[0.08] transition-all duration-1000 pointer-events-none" />

                {/* THE CARD */}
                <div className="relative bg-[#0A0A0A] rounded-[24px] md:rounded-[32px] overflow-hidden border border-white/5 transition-all duration-500 z-10">

                  {/* IMAGE AREA */}
                  <div className="h-[450px] md:h-[520px] relative overflow-hidden bg-[#0A0A0A]">

                    <img
                      src={talentNear ? item.img : undefined}
                      className="w-full h-full object-cover 
                        grayscale-0 opacity-70 
                        md:grayscale md:contrast-125 md:opacity-40 
                        md:group-hover:opacity-100 md:group-hover:grayscale-0 md:group-hover:scale-105 
                        transition-all duration-1000 ease-in-out"
                      alt={item.name}
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 via-30% to-transparent" />

                    <div className="absolute top-4 md:top-6 right-4 md:right-6 z-20">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-xl border border-white/10 shadow-lg">
                        <div className={`w-1.5 h-1.5 rounded-full ${item.type === 'REAL' ? 'bg-[#A3E635] shadow-[0_0_8px_#A3E635]' : 'bg-cyan-400 shadow-[0_0_8px_cyan]'}`} />
                        <span className="text-[9px] font-bold tracking-widest text-white/90">
                          {item.type}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* INFO AREA */}
                  <div className="absolute bottom-0 w-full p-6 md:p-8 space-y-3 md:space-y-4 z-20">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl md:text-2xl font-serif text-white tracking-tight">{item.name}</h3>
                    </div>

                    <div className="flex justify-between items-center text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
                      <span>{item.followers} Followers</span>
                      <span className="text-zinc-600">{item.vol} Vol</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: THE ROOMS */}
      <section className="min-h-screen w-full flex flex-col items-center justify-center px-6 md:px-16 bg-[#050505] py-20 md:py-32 relative overflow-hidden border-t border-white/5">

        {/* 1. ATMOSPHERIC BACKGROUND */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.05]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[40vh] bg-[#A3E635] blur-[250px] opacity-[0.04]" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">

          {/* MOBILE: Centered | DESKTOP: Space-between */}
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-12 md:mb-24 gap-6 md:gap-10 text-center md:text-left">

            {/* Title Side */}
            <div>
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em]">
                  Live & Reserved
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.9]">
                THE ROOMS<span className="text-[#A3E635]">.</span>
              </h2>
            </div>

            {/* Description Side */}
            <div className="max-w-md">
              <p className="text-zinc-400 text-sm font-mono font-normal leading-loose tracking-wide">
                Step into live streams now or reserve your spot for later. Every session is direct, <span className="text-zinc-200">unfiltered access</span>, to the talent.
              </p>
            </div>
          </div>

          {/* 3. THE "NEURAL LINK" INTERFACE */}
          <div className="relative z-10 w-full min-h-[80vh] md:h-[80vh] flex flex-col md:flex-row overflow-hidden rounded-[24px] md:rounded-[40px] border border-white/10 shadow-[0_0_100px_rgba(0,0,0,0.8)] bg-[#080808]">

            {/* LEFT PANEL: THE "EYE" (Video Feed) - ON TOP for mobile */}
            <div className="w-full md:w-[68%] h-[250px] md:h-full relative bg-black group overflow-hidden order-1">

              {/* A. REAL VIDEO PLAYER */}
              <video
                ref={necklaceRef}
                src={necklaceNear
                  ? assetUrl('ModelNecklace.mp4')
                  : undefined}
                poster={posterUrl('ModelNecklace')}
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-all duration-[3s] ease-in-out"
              />

              {/* B. The HUD (Minimal) */}
              <div className="absolute inset-0 p-4 md:p-8 flex flex-col justify-between z-20 pointer-events-none">
                {/* Top: Minimal Status Pills */}
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2 md:gap-3">

                    {/* Live Badge */}
                    <div className="h-5 md:h-6 px-2 md:px-3 bg-red-500/20 backdrop-blur-md border border-red-500/30 rounded-full flex items-center justify-center gap-1.5 md:gap-2">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_10px_red]" />
                      <span className="text-[9px] md:text-[10px] font-mono font-bold text-white tracking-widest uppercase leading-none pt-[1px]">LIVE</span>
                    </div>

                    {/* Quality Badge - Hidden on mobile */}
                    <div className="hidden md:flex h-6 px-3 bg-black/40 backdrop-blur-md border border-white/10 rounded-full items-center justify-center">
                      <span className="text-[10px] font-mono text-white/70 tracking-widest leading-none pt-[1px]">4K • 60FPS</span>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT PANEL: THE CONTROL STACK - BELOW video on mobile */}
            <div className="w-full md:w-[32%] flex-1 md:h-full bg-[#0A0A0A] border-t md:border-t-0 md:border-l border-white/10 flex flex-col relative z-20 order-2">

              {/* Header: GLOWING TEXT */}
              <div className="p-4 md:p-8 pb-2 md:pb-4">
                <div className="flex items-center gap-2 mb-2 md:mb-4">
                  <div className="w-2 h-2 bg-zinc-600 rounded-full" />
                  <span className="text-[9px] font-mono text-zinc-500 uppercase tracking-[0.2em]">System Control</span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-[#A3E635] tracking-tight leading-none drop-shadow-[0_0_15px_rgba(163,230,53,0.3)]">
                  CHANNEL <br className="hidden md:block" /> SELECTOR
                </h2>
              </div>

              {/* THE MODULES (Scrollable Area) */}
              <div className="flex-grow px-4 md:px-6 space-y-2 md:space-y-3 overflow-y-auto custom-scrollbar">

                {/* Card 1: Active Live */}
                <div className="relative p-3 md:p-4 rounded-xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-white/10 hover:border-[#A3E635]/50 transition-all cursor-pointer group/card overflow-hidden shadow-lg">
                  <div className="absolute inset-0 bg-[#A3E635]/5 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                  <div className="flex justify-between items-start mb-2 md:mb-3 relative z-10">
                    <div className="p-1.5 md:p-2 bg-[#A3E635]/10 rounded-lg">
                      <Radio size={14} className="text-[#A3E635] md:w-4 md:h-4" />
                    </div>
                    <div className="flex gap-1">
                      <div className="w-1 h-1 bg-[#A3E635] rounded-full animate-ping" />
                      <div className="w-1 h-1 bg-[#A3E635] rounded-full" />
                    </div>
                  </div>
                  <div className="relative z-10">
                    <h4 className="text-white/80 text-sm md:text-base font-semibold tracking-[-0.01em] group-hover/card:text-white transition-colors">Live Rooms</h4>
                    <p className="text-[10px] md:text-[11px] text-zinc-600 font-mono mt-1 group-hover/card:text-zinc-500 transition-colors">Connected • 12ms</p>
                  </div>
                </div>

                {/* Card 2: Upcoming Rooms */}
                <div className="relative p-3 md:p-4 rounded-xl bg-zinc-900/30 border border-white/5 hover:border-white/20 hover:bg-gradient-to-r hover:from-white/[0.08] hover:to-transparent transition-all cursor-pointer group/card">
                  <div className="flex justify-between items-start mb-2 md:mb-3">
                    <div className="p-1.5 md:p-2 bg-white/5 rounded-lg group-hover/card:bg-white/10 transition-colors">
                      <Clock size={14} className="text-white/70 group-hover/card:text-white transition-colors md:w-4 md:h-4" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-white/80 text-sm md:text-base font-semibold tracking-[-0.01em] group-hover/card:text-white transition-colors">Upcoming Rooms</h4>
                    <p className="text-[10px] md:text-[11px] text-zinc-600 font-mono mt-1 group-hover/card:text-zinc-500 transition-colors">Next: 20:00 EST</p>
                  </div>
                </div>

                {/* Card 3: Private Sessions (1:1 video call) */}
                <div className="relative p-3 md:p-4 rounded-xl bg-zinc-900/30 border border-white/5 hover:border-white/20 hover:bg-gradient-to-r hover:from-white/[0.08] hover:to-transparent transition-all cursor-pointer group/card">
                  <div className="flex justify-between items-start mb-2 md:mb-3">
                    <div className="p-1.5 md:p-2 bg-white/5 rounded-lg group-hover/card:bg-white/10 transition-colors">
                      <Lock size={14} className="text-white/70 group-hover/card:text-white transition-colors md:w-4 md:h-4" />
                    </div>
                    <Zap size={12} className="text-zinc-700 group-hover/card:text-[#A3E635] transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-white/80 text-sm md:text-base font-semibold tracking-[-0.01em] group-hover/card:text-white transition-colors">Private Sessions</h4>
                    <p className="text-[10px] md:text-[11px] text-zinc-600 font-mono mt-1 group-hover/card:text-zinc-500 transition-colors">By Request</p>
                  </div>
                </div>

                {/* Card 4: Replays - Hidden on mobile to save space */}
                <div className="hidden md:block relative p-4 rounded-xl bg-zinc-900/30 border border-white/5 hover:border-white/20 hover:bg-gradient-to-r hover:from-white/[0.08] hover:to-transparent transition-all cursor-pointer group/card">
                  <div className="flex justify-between items-start mb-3">
                    <div className="p-2 bg-white/5 rounded-lg group-hover/card:bg-white/10 transition-colors">
                      <Users size={16} className="text-white/70 group-hover/card:text-white transition-colors" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-white/80 text-base font-semibold tracking-[-0.01em] group-hover/card:text-white transition-colors">Replays</h4>
                    <p className="text-[11px] text-zinc-600 font-mono mt-1 group-hover/card:text-zinc-500 transition-colors">Past Sessions</p>
                  </div>
                </div>
              </div>


              {/* Bottom Telemetry */}
              <div className="p-4 md:p-6 mt-auto border-t border-white/5 bg-black/20">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest block mb-1">Viewers</span>
                    <span className="text-sm font-mono text-white">2,842</span>
                  </div>
                  <div>
                    <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest block mb-1">Bandwidth</span>
                    <span className="text-sm font-mono text-[#A3E635]">4.2 GB/s</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* SECTION 4: DON'T WATCH. DIRECT. */}
      <section className="min-h-screen w-full bg-[#050505] py-20 md:py-32 px-6 md:px-16 relative overflow-hidden border-t border-white/5">

        {/* 1. ATMOSPHERIC BACKGROUND */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#A3E635] blur-[200px] opacity-[0.03]" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* MOBILE: Centered | DESKTOP: Space-between */}
          <header className="mb-12 md:mb-24 flex flex-col md:flex-row justify-between items-center md:items-end gap-6 md:gap-10 text-center md:text-left">
            <div>
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4 md:mb-6">
                <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em]">
                  Live Intervention
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.9]">
                <span className="block text-zinc-600 mb-2 md:mb-3">DON'T WATCH.</span>
                <span className="block text-white">DIRECT<span className="text-[#A3E635]">.</span></span>
              </h2>
            </div>
            <div className="max-w-md">
              <p className="text-zinc-400 text-sm font-mono font-normal leading-loose tracking-wide">
                Influence what happens next. Pool funds to trigger <span className="text-zinc-200">live events</span> and reshape the experience in real‑time.
              </p>
            </div>
          </header>

          {/* 3. THE HOLOGRAPHIC COMMAND CENTER */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

            {/* LEFT COLUMN: THE CONTROLLER */}
            <div className="order-2 lg:order-1 relative z-20">

              <div className="flex justify-between items-center mb-4 md:mb-6 pb-2 border-b border-white/5">
                <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Active Pool</span>
                <span className="text-[10px] font-mono text-[#A3E635] animate-pulse">● Live Updating</span>
              </div>

              {/* A. THE WATERFALL LIST */}
              <div className="mb-6 md:mb-8 space-y-2">
                {activeRequests.map((req, i) => {
                  const Icon = req.icon;
                  return (
                    <div
                      key={req.id}
                      onMouseEnter={() => setHoveredReq(req)}
                      onMouseLeave={() => setHoveredReq(null)}
                      className={`group relative px-4 md:px-5 py-3 md:py-4 rounded-xl border transition-all duration-300 cursor-pointer overflow-hidden
          ${hoveredReq?.id === req.id
                          ? 'bg-gradient-to-r from-white/[0.08] to-transparent border-white/20 translate-x-2 shadow-2xl'
                          : 'bg-black/40 border-white/5 hover:border-white/10'}`
                      }
                    >

                      {/* 1. THE RIGHT VERTICAL LIME LINE */}
                      <div className={`absolute top-0 bottom-0 right-0 w-[3px] bg-[#A3E635] transition-opacity duration-300
          ${hoveredReq?.id === req.id ? 'opacity-100' : 'opacity-0'}`}
                      />

                      <div className="flex justify-between items-center relative z-10">
                        <div className="flex items-center gap-3 md:gap-4">

                          {/* Icon Box */}
                          <div className={`p-1.5 md:p-2 rounded-lg transition-colors ${hoveredReq?.id === req.id ? 'bg-[#A3E635] text-black' : 'bg-white/5 text-zinc-500'}`}>
                            <Icon size={14} className="md:w-4 md:h-4" />
                          </div>

                          <div>
                            <p className={`text-sm md:text-base font-normal leading-snug transition-colors duration-300 tracking-[-0.01em] ${hoveredReq?.id === req.id ? 'text-white' : 'text-zinc-400'}`}>
                              {req.title}
                            </p>

                            <div className="flex items-center gap-2 mt-1">
                              <div className="w-12 md:w-16 h-1 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-[#A3E635]" style={{ width: `${(parseFloat(req.backers) / 50) * 100}%` }} />
                              </div>
                              <span className="text-[8px] md:text-[9px] font-mono text-zinc-600">{req.backers} Contributors</span>
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <span className="block text-xs md:text-sm font-mono text-white tracking-tight">{req.current} ETH</span>
                          <span className="text-[8px] md:text-[9px] font-bold uppercase tracking-widest text-zinc-600 transition-colors">
                            {hoveredReq?.id === req.id ? 'PREVIEW' : 'PENDING'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* B. THE INPUT BAR */}
              <div className={`relative group z-20 transition-all duration-500 ${isFocused ? 'scale-[1.01]' : ''}`}>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#A3E635]/50 via-[#A3E635]/10 to-[#A3E635]/50 opacity-0 group-focus-within:opacity-100 transition-opacity duration-500 rounded-xl blur-md" />
                <div className={`relative flex items-center gap-2 md:gap-4 bg-[#0A0A0A] border transition-all duration-300 p-2 md:p-3 rounded-xl shadow-2xl ${isFocused ? 'border-[#A3E635]/50 bg-black' : 'border-white/10'}`}>
                  <div className="p-2 md:p-3 bg-white/5 rounded-lg shrink-0 border border-white/5">
                    <Plus size={16} className={`md:w-[18px] md:h-[18px] ${isFocused ? "text-[#A3E635]" : "text-zinc-500"}`} />
                  </div>
                  <input
                    type="text"
                    placeholder="Request an action..."
                    className="bg-transparent w-full text-white placeholder:text-zinc-600 outline-none font-mono text-xs md:text-sm py-2"
                    value={newRequest}
                    onChange={(e) => setNewRequest(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                  <div className="flex items-center gap-2 md:gap-3 pr-1 shrink-0">
                    <button className="h-8 md:h-9 px-3 md:px-4 bg-[#A3E635] text-black text-[9px] md:text-[10px] font-bold uppercase tracking-widest rounded-lg hover:bg-[#B4F44A] hover:shadow-[0_0_20px_rgba(163,230,53,0.4)] transition-all flex items-center gap-1 md:gap-2">
                      <span>REQUEST</span>
                      <ArrowUpRight size={10} className="md:w-3 md:h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: THE VIEWPORT */}
            <div className="order-1 lg:order-2 relative lg:sticky lg:top-10">

              <div
                ref={viewportRef}
                className={`relative aspect-[4/3] w-full rounded-[24px] md:rounded-[32px] overflow-hidden border border-white/10 bg-black transition-all duration-500
                 ${hoveredReq ? 'shadow-[0_0_80px_rgba(163,230,53,0.15)] border-[#A3E635]/30' : 'shadow-2xl'}`}>

                {/* VIEWPORT VIDEOS: backstage + the four requests, stacked and
                    mounted together; exactly one is visible (see 040-01 above). */}
                <video
                  data-viewport-video="default"
                  src={viewportNear ? assetUrl('backstage_sm.mp4') : undefined}
                  poster={posterUrl('backstage_sm')}
                  loop
                  muted
                  playsInline
                  preload={viewportNear ? 'auto' : 'none'}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-500
                    ${!hoveredReq ? 'opacity-60 grayscale' : 'opacity-0 pointer-events-none'}
                  `}
                />
                {activeRequests.map((req) => (
                  <video
                    key={req.id}
                    data-viewport-video={String(req.id)}
                    src={viewportNear ? req.video : undefined}
                    poster={req.poster}
                    loop
                    muted
                    playsInline
                    preload={warmRequests ? 'auto' : 'metadata'}
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-500
                      ${hoveredReq?.id === req.id ? 'opacity-100 grayscale-0' : 'opacity-0 pointer-events-none'}
                    `}
                  />
                ))}

                {/* HUD */}
                <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-between pointer-events-none">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-[9px] md:text-[10px] font-mono text-red-500 uppercase">Live_Feed_04</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>


      {/* SECTION 5: THE LEDGER */}
      <section className="w-full bg-[#050505] py-20 md:py-32 px-6 md:px-16 border-t border-white/5 relative overflow-hidden">

        {/* Background Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px] opacity-20 pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">

          {/* MOBILE: Centered | DESKTOP: Space-between */}
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-12 md:mb-24 gap-6 md:gap-10 text-center md:text-left">
            <div>
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em]">
                  On-Chain Records
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.9]">
                THE LEDGER<span className="text-[#A3E635]">.</span>
              </h2>
            </div>

            <div className="max-w-md">
              <p className="text-zinc-400 text-sm font-mono font-normal leading-loose tracking-wide">
                Every transaction lives on the blockchain. Track talent velocity, measure demand, and witness <span className="text-zinc-200">real-time ecosystem valuation</span>.
              </p>
            </div>
          </div>

          {/* 2. THE TERMINAL UI */}
          <div className="w-full bg-gradient-to-b from-white/[0.08] to-transparent backdrop-blur-md border border-zinc-800/40 rounded-[16px] md:rounded-[24px] overflow-hidden shadow-2xl">

            {/* Terminal Top Bar */}
            <div className="h-10 md:h-12 border-b border-white/5 flex items-center px-4 md:px-6 justify-between bg-black/40">
              {/* Colored Dots */}
              <div className="flex gap-1.5 md:gap-2">
                <div className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-red-500/20 border border-red-500/50" />
                <div className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                <div className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-green-500/20 border border-green-500/50" />
              </div>

              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A3E635] animate-pulse" />
                <span className="text-[8px] md:text-[9px] font-mono text-zinc-500 uppercase tracking-widest">Live_Feed_Active</span>
              </div>
            </div>

            {/* THE TABLE */}
            <div className="w-full">

              {/* Column Headers */}
              <div className="grid grid-cols-3 md:grid-cols-[1fr_1.2fr_2.5fr_1fr_0.8fr] gap-4 px-4 md:px-6 py-3 md:py-4 border-b border-white/[0.02]">
                <div className="text-[8px] md:text-[9px] font-mono text-zinc-500 uppercase tracking-widest flex items-center gap-1 md:gap-2">
                  <Hash size={8} className="md:w-[10px] md:h-[10px]" /> Tx Hash
                </div>
                <div className="text-[8px] md:text-[9px] font-mono text-zinc-500 uppercase tracking-widest">
                  Operation
                </div>
                <div className="hidden md:block text-[9px] font-mono text-zinc-500 uppercase tracking-widest">
                  Context
                </div>
                <div className="text-[8px] md:text-[9px] font-mono text-zinc-500 uppercase tracking-widest text-right">
                  Value
                </div>
                <div className="hidden md:block text-[9px] font-mono text-zinc-500 uppercase tracking-widest text-right">
                  Time
                </div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-white/[0.02]">
                {transactions.map((tx, idx) => (
                  <div key={idx} className="grid grid-cols-3 md:grid-cols-[1fr_1.2fr_2.5fr_1fr_0.8fr] gap-4 px-4 md:px-6 py-4 md:py-5 group hover:bg-white/[0.05] transition-colors cursor-default items-center">

                    {/* COL 1: HASH */}
                    <div className="flex items-center">
                      <span className="text-[10px] md:text-xs font-mono text-zinc-400 group-hover:text-white transition-colors">
                        {tx.id}
                      </span>
                    </div>

                    {/* COL 2: OPERATION */}
                    <div>
                      <div className="inline-flex items-center px-2 md:px-2.5 py-1 rounded-md bg-white/[0.03] border border-white/[0.05] group-hover:border-white/10 transition-colors">
                        <span className="text-[8px] md:text-[10px] font-mono text-zinc-300 tracking-wide">
                          {tx.action}
                        </span>
                      </div>
                    </div>

                    {/* COL 3: CONTEXT (Desktop only) */}
                    <div className="hidden md:block text-xs font-mono text-zinc-500 group-hover:text-zinc-400 transition-colors truncate">
                      {tx.target}
                    </div>

                    {/* COL 4: VALUE */}
                    <div className="text-right">
                      <span className={`text-[10px] md:text-xs font-mono ${tx.value.includes('+') ? 'text-[#A3E635]' : 'text-zinc-400'}`}>
                        {tx.value}
                      </span>
                    </div>

                    {/* COL 5: TIME (Desktop only) */}
                    <div className="hidden md:block text-[10px] font-mono text-zinc-600 text-right">
                      {tx.time}
                    </div>

                  </div>
                ))}
              </div>

            </div>
            {/* Terminal Footer */}
            <div className="px-4 md:px-6 py-3 md:py-4 bg-black/40 flex justify-between items-center border-t border-white/5">
              {/* Left: View Etherscan */}
              <div className="flex items-center gap-2 text-[8px] md:text-[9px] font-mono text-zinc-600 uppercase tracking-widest group cursor-pointer hover:text-[#A3E635] transition-colors">
                <span>View Etherscan</span>
                <ArrowRight size={8} className="md:w-[10px] md:h-[10px] group-hover:translate-x-0.5 transition-transform" />
              </div>

              {/* Right: Gas */}
              <span className="text-[8px] md:text-[9px] font-mono text-zinc-700">Gas: 12 gwei</span>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 6: THE ENGINE */}
      <section className="min-h-screen bg-[#050505] py-20 md:py-32 px-6 md:px-16 relative overflow-hidden border-t border-white/5 flex items-center">

        {/* Background: Digital Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:60px_60px] opacity-10 pointer-events-none" />

        <div className="max-w-7xl mx-auto w-full relative z-10">

          {/* MOBILE: Centered | DESKTOP: Space-between */}
          <header className="mb-12 md:mb-24 flex flex-col md:flex-row justify-between items-center md:items-end gap-6 md:gap-10 border-b border-white/5 pb-8 md:pb-10 text-center md:text-left">
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em]">
                  AI-Powered Discovery
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.9]">
                THE ENGINE<span className="text-[#A3E635]">.</span>
              </h2>
            </div>
            <div className="max-w-md">
              <p className="text-zinc-400 text-sm font-mono font-normal leading-loose tracking-wide">
                Intelligent discovery, real-time adaptation, and wallet-only identity. AI surfaces what resonates while your <span className="text-zinc-200">privacy stays sovereign</span>.
              </p>
            </div>

          </header>

          {/* 2. CONTENT GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-32 items-center">

            {/* LEFT: THE INTERACTIVE LIST */}
            <div className="space-y-6 md:space-y-8 relative">

              {/* The Vertical "Rail" (Left Border) */}
              <div className="absolute left-[19px] top-4 bottom-4 w-[1px] bg-white/5 hidden md:block" />

              {features.map((item, index) => {
                const isActive = activeFeature === index;
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className={`relative group cursor-pointer transition-all duration-500 ease-out pl-0 md:pl-0
                ${isActive ? 'opacity-100 md:translate-x-4' : 'opacity-40 hover:opacity-70'}`}
                    onMouseEnter={() => setActiveFeature(index)}
                  >
                    <div className="flex gap-4 md:gap-8">

                      {/* Icon Column */}
                      <div className="relative z-10 mt-1">
                        <div className={`w-8 md:w-10 h-8 md:h-10 rounded-full border flex items-center justify-center transition-all duration-500
                    ${isActive ? 'bg-[#A3E635]/10 border-[#A3E635] scale-110' : 'bg-white/5 border-white/10'}`}>
                          <Icon size={16} className={`md:w-[18px] md:h-[18px] transition-colors duration-300 ${isActive ? 'text-[#A3E635]' : 'text-zinc-400'}`} />
                        </div>
                      </div>

                      {/* Text Column */}
                      <div>
                        <h4 className={`font-bold text-lg md:text-xl tracking-tight mb-2 md:mb-4 transition-colors duration-300 ${isActive ? 'text-white' : 'text-zinc-300'}`}>
                          {item.title}
                        </h4>

                        <p className="text-zinc-400 text-sm md:text-base font-normal leading-relaxed max-w-sm transition-colors duration-300 tracking-[-0.01em]">
                          {item.desc}
                        </p>
                      </div>


                    </div>

                    {/* The "Laser Connector" Line */}
                    <div className={`hidden lg:block absolute top-6 left-full w-0 h-[1px] bg-gradient-to-r from-[#A3E635]/50 to-transparent transition-all duration-700
                 ${isActive ? 'w-32 opacity-100' : 'w-0 opacity-0'}`}
                    />

                  </div>
                );
              })}
            </div>

            {/* RIGHT: THE REACTIVE VISUAL */}
            <div className="relative flex justify-center lg:justify-end">

              {/* The Glow */}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[420px] h-[300px] md:h-[420px] bg-[#A3E635] rounded-full blur-[120px] transition-opacity duration-1000
          ${activeFeature === 0 ? 'opacity-[0.08]' : ''}
          ${activeFeature === 1 ? 'opacity-[0.12]' : ''}
          ${activeFeature === 2 ? 'opacity-[0.06]' : ''}`}
              />

              {/* The Schematic Container */}
              <div className="relative w-full aspect-square max-w-[300px] md:max-w-[460px] border border-white/10 rounded-full flex items-center justify-center animate-[spin_60s_linear_infinite]">

                {/* Inner Dashed Ring */}
                <div className="absolute inset-6 md:inset-8 border border-white/5 rounded-full border-dashed animate-[spin_40s_linear_infinite_reverse]" />

                {/* The Core */}
                <div className="w-28 md:w-40 h-28 md:h-40 bg-[#0A0A0A] border border-[#A3E635]/30 rounded-full flex items-center justify-center relative shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_0_30px_rgba(163,230,53,0.1)]">

                  {/* Dynamic Center Icon */}
                  <div className="relative z-20 transition-all duration-500 transform" key={activeFeature}>
                    {activeFeature === 0 && <Network className="text-[#A3E635] opacity-80" size={40} strokeWidth={1} />}
                    {activeFeature === 1 && <Zap className="text-[#A3E635] opacity-80" size={40} strokeWidth={1} />}
                    {activeFeature === 2 && <Brain className="text-[#A3E635] opacity-80" size={40} strokeWidth={1} />}
                  </div>

                  {/* Scanline Effect */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#A3E635]/10 to-transparent w-full h-1 top-1/2 animate-scan rounded-full overflow-hidden" />
                </div>

                {/* Orbiting Satellite */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-3 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]" />

                {/* Decorative Dots */}
                <div className="absolute bottom-12 right-12 w-1.5 h-1.5 bg-zinc-600 rounded-full" />

              </div>

              {/* Dynamic Data Overlay */}
              <div className="absolute -bottom-6 md:-bottom-10 right-4 md:right-10 lg:-right-10 bg-[#0A0A0A] border border-white/10 p-4 md:p-6 rounded-xl backdrop-blur-md z-20 shadow-2xl transition-all duration-500">
                <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                  <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${activeFeature === 2 ? 'bg-white' : 'bg-[#A3E635]'}`} />
                  <span className="text-[8px] md:text-[9px] font-mono text-white uppercase tracking-widest">
                    {activeFeature === 0 ? 'Searching...' : activeFeature === 1 ? 'Synthesizing' : 'Encrypting'}
                  </span>
                </div>
                <div className="space-y-1 md:space-y-1.5 min-w-[100px] md:min-w-[140px]">
                  <div className="flex justify-between text-[8px] md:text-[9px] font-mono text-zinc-500">
                    <span>Status</span>
                    <span className="text-zinc-300">Online</span>
                  </div>
                  <div className="flex justify-between text-[8px] md:text-[9px] font-mono text-zinc-500">
                    <span>Metric</span>
                    <span key={activeFeature} className="text-zinc-300 animate-[pulse_0.5s_ease-in-out]">{features[activeFeature].metric}</span>
                  </div>
                  <div className="flex justify-between text-[8px] md:text-[9px] font-mono text-zinc-500">
                    <span>Output</span>
                    <span key={activeFeature} className="text-[#A3E635] animate-[pulse_0.5s_ease-in-out]">{features[activeFeature].stat}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: THE XPASSES */}
      <section className="min-h-screen bg-[#050505] py-16 md:py-24 px-6 md:px-16 relative overflow-hidden border-t border-white/5 flex flex-col justify-center">

        {/* Background Ambience */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-zinc-900/20 via-[#050505] to-[#050505] pointer-events-none" />

        <div className="max-w-7xl mx-auto w-full relative z-10">

          {/* MOBILE: Centered | DESKTOP: Space-between */}
          <header className="mb-16 md:mb-32 flex flex-col md:flex-row justify-between items-center md:items-end gap-6 md:gap-10 border-b border-white/5 pb-8 md:pb-10 text-center md:text-left">
            <div>
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4 md:mb-6">
                <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em]">
                  Exclusive Access
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.9]">
                <span className="block text-white">
                  THE XPASSES<span className="text-[#A3E635]">.</span>
                </span>
              </h2>
            </div>

            {/* Right Side Description */}
            <div className="max-w-md">
              <p className="text-zinc-400 text-sm font-mono font-normal leading-loose tracking-wide">
                Creator XPasses. 100 per creator, minted once. Hold to <span className="text-zinc-200">unlock Rooms</span>, chat and livestreams — forever.
              </p>
            </div>
          </header>

          {/* 2. THE SPATIAL GRID */}
          <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-0 items-center">

            {/* --- LEFT COLUMN (Items 1 & 4) --- */}
            <div className="order-2 lg:order-1 flex flex-col items-center lg:items-end justify-center space-y-8 lg:space-y-12 lg:pr-12">

              {/* ITEM 1: ON-CHAIN SIGNAL */}
              <div
                className="group relative flex flex-col lg:flex-row items-center lg:items-center gap-4 lg:gap-6 text-center lg:text-right"
                onMouseEnter={() => setActiveItem(0)}
                onMouseLeave={() => setActiveItem(null)}
              >

                <div className="space-y-2 lg:space-y-3 max-w-[280px]">
                  <h4 className={`font-bold text-lg lg:text-xl tracking-tight transition-colors duration-300 ${activeItem === 0 ? 'text-white' : 'text-zinc-300'}`}>
                    On-Chain Signal
                  </h4>

                  <p className="text-zinc-400 text-sm lg:text-base font-normal leading-relaxed transition-colors duration-300 tracking-[-0.01em]">
                    XPass activity is a live momentum indicator — spot rising creators before the crowd.
                  </p>
                </div>

                {/* Icon & Connector Line (Desktop Only) */}
                <div className="flex items-center">
                  <div className={`hidden lg:block w-16 h-[1px] transition-all duration-500 ${activeItem === 0 ? 'bg-[#A3E635] w-24' : 'bg-white/10'}`} />
                  <div className={`w-10 h-10 rounded-full border flex items-center justify-center relative z-10 transition-all duration-500
              ${activeItem === 0 ? 'bg-[#A3E635]/10 border-[#A3E635] scale-110' : 'bg-[#050505] border-white/10'}`}>
                    <Activity size={16} className={`transition-colors duration-300 ${activeItem === 0 ? 'text-[#A3E635]' : 'text-zinc-500'}`} />
                  </div>
                </div>
              </div>

              {/* ITEM 4: OPEN MARKET */}
              <div
                className="group relative flex flex-col lg:flex-row items-center lg:items-center gap-4 lg:gap-6 text-center lg:text-right"
                onMouseEnter={() => setActiveItem(3)}
                onMouseLeave={() => setActiveItem(null)}
              >

                <div className="space-y-2 lg:space-y-3 max-w-[280px]">
                  <h4 className={`font-bold text-lg lg:text-xl tracking-tight transition-colors duration-300 ${activeItem === 3 ? 'text-white' : 'text-zinc-300'}`}>
                    Open Market
                  </h4>

                  <p className="text-zinc-400 text-sm lg:text-base font-normal leading-relaxed transition-colors duration-300 tracking-[-0.01em]">
                    List your XPass for sale any time. The consumable balance passes to whoever owns it next.
                  </p>
                </div>

                {/* Icon & Connector Line (Desktop Only) */}
                <div className="flex items-center">
                  <div className={`hidden lg:block w-16 h-[1px] transition-all duration-500 ${activeItem === 3 ? 'bg-[#A3E635] w-24' : 'bg-white/10'}`} />
                  <div className={`w-10 h-10 rounded-full border flex items-center justify-center relative z-10 transition-all duration-500
              ${activeItem === 3 ? 'bg-[#A3E635]/10 border-[#A3E635] scale-110' : 'bg-[#050505] border-white/10'}`}>
                    <Tag size={16} className={`transition-colors duration-300 ${activeItem === 3 ? 'text-[#A3E635]' : 'text-zinc-500'}`} />
                  </div>
                </div>
              </div>
            </div>

            {/* --- CENTER COLUMN (The Monolith) --- */}
            <div className="order-1 lg:order-2 flex justify-center relative z-20 my-8 lg:my-0">
              <div className="relative perspective-1000 group w-full max-w-[280px] md:max-w-[350px]">

                {/* Reactive Glow behind card */}
                <div className={`absolute inset-0 rounded-[32px] blur-[80px] transition-opacity duration-700 pointer-events-none
             ${activeItem !== null ? 'opacity-30 bg-[#A3E635]' : 'opacity-25 bg-white'}`}
                />

                {/* The Card */}
                <div className="relative w-full h-[400px] md:h-[500px] bg-[#030303] rounded-[20px] md:rounded-[24px] border border-white/5 shadow-2xl flex flex-col justify-between overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/90 opacity-80 pointer-events-none" />

                  {/* Top */}
                  <div className="p-6 md:p-8 flex justify-between items-start z-10">
                    <span className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Series_01</span>
                  </div>

                  {/* Center */}
                  <div className="px-6 md:px-8 z-10">
                    <h3 className="text-2xl md:text-3xl font-mono text-transparent bg-clip-text bg-gradient-to-b from-white via-zinc-400 to-zinc-600 tracking-wider">
                      0x38...B29
                    </h3>
                    <p className="mt-2 text-[9px] font-mono text-zinc-600 uppercase tracking-[0.3em]">
                      Creator XPass
                    </p>
                  </div>

                  {/* Bottom */}
                  <div className="p-6 md:p-8 z-10 mt-auto">
                    <div className="flex justify-between items-end border-t border-white/5 pt-4 md:pt-6">
                      <div>
                        <p className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest mb-1">Access Rights</p>
                        <p className="text-sm font-mono text-zinc-300">All Access</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                        <Fingerprint size={20} className="text-zinc-500" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {/* --- RIGHT COLUMN (Items 2 & 3) --- */}
            <div className="order-3 flex flex-col items-center lg:items-start justify-center space-y-8 lg:space-y-16 lg:pl-12">

              {/* ITEM 2: MEMBERSHIP ACCESS */}
              <div
                className="group relative flex flex-col lg:flex-row-reverse items-center lg:items-center gap-4 lg:gap-6 text-center lg:text-left"
                onMouseEnter={() => setActiveItem(1)}
                onMouseLeave={() => setActiveItem(null)}
              >
                <div className="space-y-2 lg:space-y-3 max-w-[280px]">
                  <h4 className={`font-bold text-lg lg:text-xl tracking-tight transition-colors duration-300 ${activeItem === 1 ? 'text-white' : 'text-zinc-300'}`}>
                    Membership Access
                  </h4>

                  <p className="text-zinc-400 text-sm lg:text-base font-normal leading-relaxed transition-colors duration-300 tracking-[-0.01em]">
                    Hold 1 Creator XPass to unlock their Rooms, chat, livestreams and every replay.
                  </p>
                </div>

                {/* Connector */}
                <div className="flex items-center flex-row-reverse">
                  <div className={`hidden lg:block w-16 h-[1px] transition-all duration-500 ${activeItem === 1 ? 'bg-[#A3E635] w-24' : 'bg-white/10'}`} />
                  <div className={`w-10 h-10 rounded-full border flex items-center justify-center relative z-10 transition-all duration-500
        ${activeItem === 1 ? 'bg-[#A3E635]/10 border-[#A3E635] scale-110' : 'bg-[#050505] border-white/10'}`}>
                    <Lock size={16} className={`transition-colors duration-300 ${activeItem === 1 ? 'text-[#A3E635]' : 'text-zinc-500'}`} />
                  </div>
                </div>
              </div>

              {/* ITEM 3: FOUNDING 100 */}
              <div
                className="group relative flex flex-col lg:flex-row-reverse items-center lg:items-center gap-4 lg:gap-6 text-center lg:text-left"
                onMouseEnter={() => setActiveItem(2)}
                onMouseLeave={() => setActiveItem(null)}
              >
                <div className="space-y-2 lg:space-y-3 max-w-[280px]">
                  <h4 className={`font-bold text-lg lg:text-xl tracking-tight transition-colors duration-300 ${activeItem === 2 ? 'text-white' : 'text-zinc-300'}`}>
                    Carried Value
                  </h4>

                  <p className="text-zinc-400 text-sm lg:text-base font-normal leading-relaxed transition-colors duration-300 tracking-[-0.01em]">
                    10% of everything you spend with a creator comes back to your XPass, ready to spend with them again.
                  </p>
                </div>

                {/* Connector */}
                <div className="flex items-center flex-row-reverse">
                  <div className={`hidden lg:block w-16 h-[1px] transition-all duration-500 ${activeItem === 2 ? 'bg-[#A3E635] w-24' : 'bg-white/10'}`} />
                  <div className={`w-10 h-10 rounded-full border flex items-center justify-center relative z-10 transition-all duration-500
        ${activeItem === 2 ? 'bg-[#A3E635]/10 border-[#A3E635] scale-110' : 'bg-[#050505] border-white/10'}`}>
                    <DollarSign size={16} className={`transition-colors duration-300 ${activeItem === 2 ? 'text-[#A3E635]' : 'text-zinc-500'}`} />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="w-full bg-[#050505] border-t border-white/5 py-12 md:py-16 px-6 md:px-16 relative">
        <div className="max-w-7xl mx-auto">

          {/* Single Row Layout */}
          <div className="flex flex-col md:flex-row justify-between items-center md:items-center gap-6 md:gap-8 text-center md:text-left">

            {/* Left: Protocol Name (Small) */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                Lunexx Protocol © 2026
              </span>
            </div>


            <div className="flex flex-col md:items-end gap-4">
              {/* Horizontal Links */}
              <div className="flex items-center gap-6">
                {[
                  // Twitter and Etherscan are still placeholders -- they keep
                  // the '#' until there is a real destination, rather than
                  // pointing somewhere plausible-but-wrong.
                  { label: 'Twitter', href: '#' },
                  { label: 'Instagram', href: 'https://www.instagram.com/lunexxai/' },
                  { label: 'Etherscan', href: '#' },
                ].map(({ label, href }) => {
                  // rel="noopener" is not optional on a target="_blank" link:
                  // without it the opened page receives window.opener and can
                  // navigate this tab somewhere else.
                  const external = href.startsWith('http');
                  const openInNewTab = external
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {};
                  return (
                    <a
                      key={label}
                      href={href}
                      {...openInNewTab}
                      className="text-[10px] font-mono text-zinc-600 hover:text-[#A3E635] uppercase tracking-widest transition-colors duration-300"
                    >
                      {label}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </footer>

    </div >
  );
}